import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { 
  Check, 
  CreditCard, 
  Shield, 
  Users, 
  BarChart3, 
  Volume2, 
  MessageCircle,
  BookOpen,
  FileText,
  Heart,
  Brain,
  Zap,
  Star,
  Clock,
  Lock,
  Globe,
  Smartphone,
  Headphones,
  TrendingUp,
  Award,
  Eye,
  ArrowRightLeft,
  AlertTriangle,
  Calendar,
  Target,
  Palette,
  Settings,
  HelpCircle,
  Phone,
  Mail,
  Video,
  Mic,
  MicOff,
  Download,
  Wifi,
  WifiOff
} from 'lucide-react';

const Subscription = () => {
  const { 
    subscription, 
    trialInfo,
    isOnTrial, 
    isTrialExpired, 
    getTrialDaysRemaining,
    createSubscription, 
    cancelSubscription, 
    updateSubscription, 
    getPlanFeatures 
  } = useSubscription();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (planType) => {
    setLoading(true);
    setError('');

    console.log('Attempting to subscribe to plan:', planType);

    const result = await createSubscription(planType);
    
    console.log('Subscription result:', result);
    
    if (result.success) {
      console.log('Redirecting to checkout URL:', result.checkoutUrl);
      // Redirect to Stripe checkout
      window.location.href = result.checkoutUrl;
    } else {
      console.error('Subscription failed:', result.error);
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
      setLoading(true);
      setError('');

      const result = await cancelSubscription();
      
      if (result.success) {
        navigate('/app');
      } else {
        setError(result.error);
      }
      
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');

    const result = await updateSubscription('advanced');
    
    if (result.success) {
      navigate('/app');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };





  const plans = [
    {
      type: 'base',
      name: 'Base Plan',
      price: '$9.99',
      period: 'month',
      description: 'Perfect for getting started with AI therapy',
      badge: null,
      features: [
        {
          category: 'AI Therapy',
          items: [
            { text: 'Access to 1 specialized AI therapist', icon: MessageCircle },
            { text: '24/7 unlimited chat sessions', icon: Clock },
            { text: 'Crisis detection & emergency alerts', icon: AlertTriangle },
            { text: 'Evidence-based therapeutic approaches', icon: Brain },
          ]
        },
        {
          category: 'Progress Tracking',
          items: [
            { text: 'Basic mood tracking', icon: Heart },
            { text: 'Session history & analytics', icon: BarChart3 },
            { text: 'Progress visualization', icon: TrendingUp },
            { text: 'Goal setting & monitoring', icon: Target },
          ]
        },
        {
          category: 'Assessments & Tools',
          items: [
            { text: 'Mental health assessments', icon: FileText },
            { text: 'Personal journaling', icon: BookOpen },
            { text: 'Guided meditation sessions', icon: Headphones },
            { text: 'Coping strategy library', icon: Palette },
          ]
        },
        {
          category: 'Support & Security',
          items: [
            { text: 'Email support', icon: Mail },
            { text: 'HIPAA-compliant security', icon: Shield },
            { text: 'Data encryption', icon: Lock },
            { text: 'Privacy controls', icon: Eye },
          ]
        }
      ],
      popular: false
    },
    {
      type: 'advanced',
      name: 'Advanced Plan',
      price: '$19.99',
      period: 'month',
      description: 'Comprehensive therapy with cross-referencing & advanced features',
      badge: 'Most Popular',
      features: [
        {
          category: 'AI Therapy',
          items: [
            { text: 'Access to ALL 10 specialized AI therapists', icon: Users },
            { text: 'Cross-referencing across all therapists', icon: ArrowRightLeft },
            { text: 'Advanced crisis detection & response', icon: AlertTriangle },
            { text: 'Personalized therapy recommendations', icon: Brain },
            { text: 'Therapist handover & collaboration', icon: ArrowRightLeft },
          ]
        },
        {
          category: 'Voice & Multimedia',
          items: [
            { text: 'Voice-to-text chat support', icon: Mic },
            { text: 'Text-to-speech responses', icon: Volume2 },
            { text: 'Audio message recording', icon: Mic },
            { text: 'Voice session capabilities', icon: Headphones },
          ]
        },
        {
          category: 'Advanced Analytics',
          items: [
            { text: 'AI-powered mood analysis', icon: Brain },
            { text: 'Advanced progress tracking', icon: TrendingUp },
            { text: 'Behavioral pattern recognition', icon: BarChart3 },
            { text: 'Personalized insights & recommendations', icon: Zap },
            { text: 'Long-term progress reports', icon: Calendar },
          ]
        },
        {
          category: 'Premium Features',
          items: [
            { text: 'Priority customer support', icon: Star },
            { text: 'Phone & video support', icon: Phone },
            { text: 'Custom therapy goals', icon: Target },
            { text: 'Advanced journaling with AI insights', icon: BookOpen },
            { text: 'Meditation playlist customization', icon: Headphones },
            { text: 'Peer support community access', icon: Users },
          ]
        },
        {
          category: 'Mobile & Offline',
          items: [
            { text: 'Progressive Web App (PWA)', icon: Smartphone },
            { text: 'Install to home screen', icon: Download },
            { text: 'Offline content access', icon: WifiOff },
            { text: 'Mobile-optimized experience', icon: Globe },
          ]
        },
        {
          category: 'Security & Privacy',
          items: [
            { text: 'Enhanced data protection', icon: Shield },
            { text: 'Advanced privacy controls', icon: Eye },
            { text: 'Data export capabilities', icon: Download },
            { text: 'Granular consent management', icon: Settings },
          ]
        }
      ],
      popular: true
    }
  ];

  const renderFeatureCategory = (category) => (
    <div key={category.category} className="mb-6">
      <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
        {category.category}
      </h4>
      <div className="space-y-3">
        {category.items.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div key={index} className="flex items-start space-x-3">
              <Icon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700">{feature.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-8 px-4">
      {/* Trial Status Banner */}
      {isOnTrial() && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Free Trial Active</h3>
                <p className="text-gray-600">
                  {getTrialDaysRemaining()} day{getTrialDaysRemaining() !== 1 ? 's' : ''} remaining in your free trial
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Trial ends</p>
              <p className="font-semibold text-gray-900">
                {trialInfo?.trialEnd ? new Date(trialInfo.trialEnd).toLocaleDateString() : 'Soon'}
              </p>
            </div>
          </div>
        </div>
      )}

      {isTrialExpired() && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Free Trial Expired</h3>
              <p className="text-gray-600">
                Your free trial has ended. Choose a subscription plan to continue accessing all features.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          {isTrialExpired() ? 'Continue Your Therapy Journey' : 'Choose Your Therapy Journey'}
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          {isTrialExpired() 
            ? 'Your free trial has ended. Select a subscription plan to continue your mental health journey with full access to all features.'
            : 'Select the plan that best fits your mental health needs. All plans include crisis detection, 24/7 access to AI therapists, and evidence-based therapeutic approaches.'
          }
        </p>
      </div>

      {/* Current Subscription Status */}
      {subscription && subscription.status === 'active' && (
        <div className="card p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Current Subscription</h3>
              <div className="flex items-center space-x-4">
                <span className="text-lg text-gray-700">
                  {subscription.plan === 'advanced' ? 'Advanced Plan' : 'Base Plan'}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  subscription.status === 'active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {subscription.status === 'active' ? 'Active' : 'Inactive'}
                </span>
                {subscription.currentPeriodEnd && (
                  <span className="text-sm text-gray-500">
                    Renews: {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              {subscription.plan === 'base' && (
                <button
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="btn-secondary disabled:opacity-50 flex items-center space-x-2"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>Upgrade to Advanced</span>
                </button>
              )}
              <button
                onClick={handleCancel}
                disabled={loading}
                className="btn-outline disabled:opacity-50 flex items-center space-x-2"
              >
                <Settings className="w-4 h-4" />
                <span>Manage Subscription</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}



      {/* Plans Grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        {plans.map((plan) => (
          <div
            key={plan.type}
            className={`card p-8 relative ${
              plan.popular ? 'ring-2 ring-primary-500 shadow-xl' : 'shadow-lg'
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-500 text-white px-6 py-2 rounded-full text-sm font-medium flex items-center space-x-1">
                  <Star className="w-4 h-4" />
                  <span>{plan.badge}</span>
                </span>
              </div>
            )}

            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{plan.name}</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                <span className="text-gray-600 text-lg">/{plan.period}</span>
              </div>
              <p className="text-gray-600 text-lg">{plan.description}</p>
            </div>

            <div className="space-y-6 mb-8">
              {plan.features.map(renderFeatureCategory)}
            </div>

            <button
              onClick={() => handleSubscribe(plan.type)}
              disabled={loading || (subscription && subscription.status === 'active' && subscription.plan === plan.type)}
              className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 ${
                subscription && subscription.status === 'active' && subscription.plan === plan.type
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  : plan.popular
                  ? 'bg-gradient-to-r from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                  : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1'
              }`}
            >
              {subscription && subscription.status === 'active' && subscription.plan === plan.type
                ? 'Current Plan'
                : loading
                ? 'Processing...'
                : `Choose ${plan.name}`}
            </button>
          </div>
        ))}
      </div>

      {/* Detailed Feature Comparison */}
      <div className="card p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Detailed Feature Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900 text-lg">Feature Category</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900 text-lg">Base Plan</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900 text-lg">Advanced Plan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr className="bg-gray-50">
                <td className="py-4 px-6 font-medium text-gray-900">AI Therapists</td>
                <td className="text-center py-4 px-6">1 therapist</td>
                <td className="text-center py-4 px-6">All 10 therapists</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-medium text-gray-900">Cross-Referencing</td>
                <td className="text-center py-4 px-6">
                  <span className="text-red-500 text-xl">✗</span>
                </td>
                <td className="text-center py-4 px-6">
                  <span className="text-green-500 text-xl">✓</span>
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td className="py-4 px-6 font-medium text-gray-900">Voice Features</td>
                <td className="text-center py-4 px-6">
                  <span className="text-red-500 text-xl">✗</span>
                </td>
                <td className="text-center py-4 px-6">
                  <span className="text-green-500 text-xl">✓</span>
                </td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-medium text-gray-900">AI Mood Analysis</td>
                <td className="text-center py-4 px-6">Basic</td>
                <td className="text-center py-4 px-6">Advanced AI</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="py-4 px-6 font-medium text-gray-900">Peer Support</td>
                <td className="text-center py-4 px-6">
                  <span className="text-red-500 text-xl">✗</span>
                </td>
                <td className="text-center py-4 px-6">
                  <span className="text-green-500 text-xl">✓</span>
                </td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-medium text-gray-900">PWA Installation</td>
                <td className="text-center py-4 px-6">
                  <span className="text-red-500 text-xl">✗</span>
                </td>
                <td className="text-center py-4 px-6">
                  <span className="text-green-500 text-xl">✓</span>
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td className="py-4 px-6 font-medium text-gray-900">Customer Support</td>
                <td className="text-center py-4 px-6">Email only</td>
                <td className="text-center py-4 px-6">Priority + Phone</td>
              </tr>
              <tr>
                <td className="py-4 px-6 font-medium text-gray-900">Offline Access</td>
                <td className="text-center py-4 px-6">
                  <span className="text-red-500 text-xl">✗</span>
                </td>
                <td className="text-center py-4 px-6">
                  <span className="text-green-500 text-xl">✓</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Trust & Security Features */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="text-center">
          <Shield className="w-12 h-12 text-primary-500 mx-auto mb-4" />
          <h4 className="font-semibold text-gray-900 mb-2">HIPAA Compliant</h4>
          <p className="text-gray-600 text-sm">
            Enterprise-grade security with full HIPAA compliance for your privacy.
          </p>
        </div>
        <div className="text-center">
          <CreditCard className="w-12 h-12 text-primary-500 mx-auto mb-4" />
          <h4 className="font-semibold text-gray-900 mb-2">Secure Payments</h4>
          <p className="text-gray-600 text-sm">
            Stripe-powered payments with bank-level encryption and fraud protection.
          </p>
        </div>
        <div className="text-center">
          <Clock className="w-12 h-12 text-primary-500 mx-auto mb-4" />
          <h4 className="font-semibold text-gray-900 mb-2">24/7 Availability</h4>
          <p className="text-gray-600 text-sm">
            AI therapists available around the clock, whenever you need support.
          </p>
        </div>
        <div className="text-center">
          <Award className="w-12 h-12 text-primary-500 mx-auto mb-4" />
          <h4 className="font-semibold text-gray-900 mb-2">Evidence-Based</h4>
          <p className="text-gray-600 text-sm">
            Therapeutic approaches backed by clinical research and best practices.
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="card p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Frequently Asked Questions</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Can I switch plans anytime?</h4>
            <p className="text-gray-600 text-sm">Yes! You can upgrade to Advanced anytime, or downgrade at your next billing cycle.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Is there a free trial?</h4>
            <p className="text-gray-600 text-sm">New users get a 30-day free trial with the Base Plan to experience our platform.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Can I cancel anytime?</h4>
            <p className="text-gray-600 text-sm">Absolutely. Cancel anytime with no hidden fees or cancellation charges.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">What payment methods do you accept?</h4>
            <p className="text-sm">All major credit cards, debit cards, and digital wallets through secure Stripe processing.</p>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="text-center bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <AlertTriangle className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
        <p className="text-sm text-yellow-800 leading-relaxed">
          <strong>Important Disclaimer:</strong> Sabiwabee is not a substitute for professional therapy. 
          For emergencies, call 988 (US) or your local crisis hotline. 
          Subscription fees are billed monthly and can be cancelled at any time.
        </p>
      </div>
    </div>
  );
};

export default Subscription; 